import { useEffect, useState } from "react";
import { X, DownloadSimple, Share, DeviceMobile } from "@phosphor-icons/react";

const NUDGE_KEY = "fp_app_install_nudge_done_v1";

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator?.standalone === true
  );
}

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

/** One-time in-app nudge for logged-in drivers: put FreightPilot on the home screen. */
export default function AppInstallNudge() {
  const [visible, setVisible] = useState(false);
  const [deferred, setDeferred] = useState(null);

  useEffect(() => {
    try {
      if (isStandalone() || localStorage.getItem(NUDGE_KEY)) return;
    } catch {
      return;
    }
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    setVisible(true);
    const onInstalled = () => dismiss();
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(NUDGE_KEY, new Date().toISOString()); } catch {}
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice;
    dismiss();
  };

  if (!visible) return null;

  return (
    <div
      data-testid="app-install-nudge"
      className="mx-4 sm:mx-6 lg:mx-8 mt-4 rounded-xl border border-[#FF5A1F]/30 bg-[#FF5A1F]/[0.07] p-3.5 flex items-center gap-3 flex-wrap"
    >
      <DeviceMobile size={20} weight="fill" className="text-[#FF5A1F] shrink-0" />
      <div className="flex-1 min-w-[220px]">
        <div className="text-sm font-semibold text-white">Put FreightPilot on your home screen</div>
        <div className="text-xs text-neutral-400 mt-0.5">
          {deferred ? (
            "One tap — opens like a real app with its own icon. No browser bar."
          ) : isIOS() ? (
            <>In Safari: tap <Share size={11} className="inline align-middle" /> then <b className="text-neutral-200">Add to Home Screen</b>. You'll get the app icon.</>
          ) : (
            <>In your browser menu, choose <b className="text-neutral-200">Install app</b> (or "Add to Home Screen" on your phone).</>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {deferred && (
          <button
            onClick={install}
            data-testid="app-install-nudge-install"
            className="rounded-full px-4 py-2 text-xs font-semibold bg-[#FF5A1F] text-black hover:bg-[#FF7038] transition-colors inline-flex items-center gap-1.5"
          >
            <DownloadSimple size={13} weight="bold" /> Install
          </button>
        )}
        <button
          onClick={dismiss}
          data-testid="app-install-nudge-dismiss"
          aria-label="Dismiss install tip"
          className="p-1.5 text-neutral-500 hover:text-white transition-colors"
        >
          <X size={15} />
        </button>
      </div>
    </div>
  );
}
